<!-- 
 * @Author       : pan
 * @Date         : 2025-07-11 10:00:00
 * @LastEditors  : pan
 * @LastEditTime : 2025-07-11 10:00:00
 * @FilePath     : app/javascript/dashboard/routes/dashboard/settings/blacklist/components/IPBlacklistTable.vue
 * @Description  : IP 黑名单表格组件
 * @Description  : IP 黑名单删除 404 问题修复
 * Copyright 2025 Bruce, All Rights Reserved.
 * 2025-07-11 10:00:00
  -->
<script setup>
import { computed, h, ref } from 'vue';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { getNoteTimeOption } from './AddIpRange.vue';
import { getCurrentLocale, formatDateByLocale } from './BlacklistUtils';

import Spinner from 'shared/components/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import Table from 'dashboard/components/table/Table.vue';
import Pagination from 'dashboard/components/table/Pagination.vue';
import WootButton from 'dashboard/components/ui/WootButton.vue';
import DeleteModal from 'dashboard/components/widgets/modal/DeleteModal.vue';
import { useAlert } from 'dashboard/composables';

const { blacklist, pageIndex, isLoading, totalCount } = defineProps({
  blacklist: {
    type: Array,
    default: () => [],
  },
  pageIndex: {
    type: Number,
    default: 0,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  totalCount: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['pageChange', 'deleteBlacklist', 'editBlacklist']);
const { t } = useI18n();
const store = useStore();

// 确认删除弹窗状态
const showDeleteModal = ref(false);
const selectedBlacklistId = ref(null);
// loading
const isDeleteLoading = ref(false);

// 编辑弹窗状态
const editingBlacklist = ref(null);

// 打开编辑弹窗
const openEditModal = blacklist => {
  editingBlacklist.value = { ...blacklist };
  // 将黑名单数据传递给父组件
  emit('editBlacklist', blacklist);
};

// 选中的行ID集合
const selectedRows = ref(new Set());
// 是否全选
const selectAll = ref(false);

// 打开删除确认弹窗
const openDeleteModal = id => {
  selectedBlacklistId.value = id;
  showDeleteModal.value = true;
};

// 关闭删除确认弹窗
const closeDeleteModal = () => {
  showDeleteModal.value = false;
};

// 确认删除黑名单
const confirmDelete = async () => {
  isDeleteLoading.value = true;
  try {
    if (selectedBlacklistId.value) {
      // 单个删除
      await store.dispatch('blacklist/deleteIp', selectedBlacklistId.value);
    } else if (selectedRows.value.size > 0) {
      // 批量删除
      await store.dispatch(
        'blacklist/bulkDeleteIp',
        Array.from(selectedRows.value)
      );
    }

    const successMessage = selectedBlacklistId.value
      ? t('BLACKLIST.ALERTS.IP_DELETE_SUCCESS')
      : t('BLACKLIST.ALERTS.IP_BATCH_DELETE_SUCCESS');
    useAlert(successMessage);
  } catch (error) {
    // 优化点：专门处理 404 错误
    if (error.response && error.response.status === 404) {
      const successMessage = selectedBlacklistId.value
        ? t('BLACKLIST.ALERTS.IP_DELETE_SUCCESS')
        : t('BLACKLIST.ALERTS.IP_BATCH_DELETE_SUCCESS');
      useAlert(successMessage);
    } else {
      // 对于其他所有非404的真实错误，显示通用失败提示
      const failureMessage = selectedBlacklistId.value
        ? t('BLACKLIST.ALERTS.IP_DELETE_FAILURE')
        : t('BLACKLIST.ALERTS.IP_BATCH_DELETE_FAILURE');
      useAlert(failureMessage);
    }
  } finally {
    // 无论成功、失败还是404，最终都需要执行这些清理操作
    emit('deleteBlacklist'); // 始终触发父组件刷新列表
    closeDeleteModal();
    selectedRows.value.clear();
    selectAll.value = false;
    isDeleteLoading.value = false;
  }
};

// 处理行选择
const toggleRowSelection = rowId => {
  if (selectedRows.value.has(rowId)) {
    selectedRows.value.delete(rowId);
  } else {
    selectedRows.value.add(rowId);
  }
  // 更新全选状态
  updateSelectAllState();
};

// 处理全选/取消全选
const toggleSelectAll = () => {
  if (selectAll.value) {
    // 取消全选
    selectedRows.value.clear();
  } else {
    // 全选
    blacklist.forEach(item => {
      selectedRows.value.add(item.id);
    });
  }
  selectAll.value = !selectAll.value;
};

// 取消所有选择
const toggleSelectCancelAll = () => {
  selectedRows.value.clear();
  selectAll.value = false;
};

// 更新全选状态
const updateSelectAllState = () => {
  selectAll.value =
    blacklist.length > 0 && selectedRows.value.size === blacklist.length;
};

// 获取选中的行数
const selectedRowCount = computed(() => selectedRows.value.size);

// 计算总记录数
const totalCountComputed = computed(() => totalCount);

// IP地址段单元格渲染函数
const ipRangeCellRender = cellProps => {
  const row = cellProps.row.original;
  const start = row.blocked_ip_start;
  const end = row.blocked_ip_end;

  if (!start && !end) return h('span', {}, '---');

  // 如果起始IP和结束IP相同，只显示一个值
  if (start === end) {
    return h('span', {}, start);
  }

  // 否则显示IP范围
  return h('span', {}, `${start} - ${end}`);
};

// 日期单元格渲染函数
const dateCellRender = cellProps => {
  const value = cellProps.getValue();
  if (!value) {
    return h('span', { class: 'text-slate-300 dark:text-slate-700' }, '---');
  }

  try {
    // 获取当前项目设置的语言环境
    const locale = getCurrentLocale();

    // 使用国际化日期格式化方法
    const formattedDate = formatDateByLocale(value, locale);

    return h('span', {}, formattedDate);
  } catch (error) {
    // 如果出错，回退到原来的格式化方式
    // 如果是时间戳或其他格式，转换为 Date 对象
    const date = new Date(value);

    // 格式化为 yyyy-MM-dd HH:mm:ss
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    const formattedDate = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;

    return h('span', {}, formattedDate);
  }
};

// 拦截时长单元格渲染函数
const blockDurationCellRender = cellProps => {
  const row = cellProps.row.original;
  const note = row.note;

  if (!note) {
    return h('span', { class: 'text-slate-300 dark:text-slate-700' }, '---');
  }

  // 从note获取拦截时间选项ID
  const timeOptionId = getNoteTimeOption(note);

  // 直接使用 t 函数获取国际化文本
  const { t } = useI18n();
  const displayName = t(
    `BLACKLIST.IP_BLACKLIST.TIME_OPTIONS.${timeOptionId.toUpperCase()}`
  );

  // 选项类型设置样式
  let className = 'text-xs font-medium';

  return h('span', { class: className }, displayName);
};

// 操作列单元格渲染函数
const actionCellRender = cellProps => {
  const blacklist = cellProps.row.original;

  return h('div', { class: 'flex justify-left space-x-2' }, [
    // 编辑按钮
    h(
      WootButton,
      {
        variant: 'primary',
        size: 'small',
        class:
          'text-center bg-blue-500 hover:bg-blue-600 text-white px-4 py-1 rounded-md',
        onClick() {
          openEditModal(blacklist);
        },
      },
      () => h('span', t('BLACKLIST.EDIT'))
    ),
    // 删除按钮
    h(
      WootButton,
      {
        variant: 'primary',
        size: 'small',
        class: 'text-center text-white px-4 py-1 rounded-md',
        colorScheme: 'alert',
        onClick() {
          openDeleteModal(blacklist.id);
        },
      },
      () => h('span', t('BLACKLIST.DELETE'))
    ),
  ]);
};

// 操作者信息单元格渲染函数
const operationCellRender = cellProps => {
  const row = cellProps.row.original;
  const operation = row.operation_ip || '---';
  const name = row.user_name || '---';

  return h('div', { class: 'flex flex-col' }, [
    h('span', { class: 'font-medium text-sm' }, name),
    h('span', { class: 'text-sm text-slate-600 mt-1' }, operation),
  ]);
};

// 使用计算属性动态创建列配置，确保国际化文本正确加载
const columns = computed(() => {
  const columnHelper = createColumnHelper();
  return [
    // 复选框列
    columnHelper.display({
      id: 'selection',
      header: () => null, // 不显示标题
      cell: cellProps => {
        const rowId = cellProps.row.original.id;
        return h('input', {
          type: 'checkbox',
          checked: selectedRows.value.has(rowId),
          onChange: () => toggleRowSelection(rowId),
          class:
            'w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600',
        });
      },
      size: 40,
    }),
    columnHelper.accessor('blocked_ip_start', {
      header: t('BLACKLIST.IP_BLACKLIST.TITLE'),
      cell: ipRangeCellRender,
      size: 250,
    }),
    columnHelper.accessor('blocked_type', {
      header: t('BLACKLIST.IP_BLACKLIST.BLOCK_TIME'),
      cell: blockDurationCellRender,
      size: 150,
    }),
    columnHelper.accessor('created_at', {
      header: t('BLACKLIST.IP_BLACKLIST.BLOCKED_AT'),
      cell: dateCellRender,
      size: 180,
    }),
    columnHelper.accessor('operation_ip', {
      header: t('BLACKLIST.IP_BLACKLIST.OPERATION_IP'),
      cell: operationCellRender,
      size: 150,
    }),
    columnHelper.display({
      id: 'actions',
      header: t('BLACKLIST.IP_BLACKLIST.SINGLE_LINE_OPERATION'),
      cell: actionCellRender,
      size: 100,
    }),
  ];
});

const paginationParams = computed(() => {
  return {
    pageIndex: pageIndex,
    pageSize: 30,
  };
});

const table = useVueTable({
  get data() {
    return blacklist || [];
  },
  get columns() {
    return columns.value;
  },
  manualPagination: true,
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
  get rowCount() {
    return totalCountComputed.value;
  },
  state: {
    get pagination() {
      return paginationParams.value;
    },
  },
  onPaginationChange: updater => {
    const newPagination = updater(paginationParams.value);
    emit('pageChange', newPagination.pageIndex);
  },
});
</script>

<template>
  <div class="blacklist-table-container">
    <div class="mb-4 p-2 bg-blue-50 rounded-md flex items-center h-10">
      <div class="flex items-center pl-3">
        <input
          type="checkbox"
          :checked="selectAll"
          @change="toggleSelectAll"
          class="mr-2 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
        />
        <span
          class="text-sm"
          :class="selectedRowCount > 0 ? 'text-blue-700' : 'text-slate-500'"
        >
          {{ $t('BLACKLIST.SELECTED_COUNT', { count: selectedRowCount }) }}
        </span>
      </div>
      <div class="ml-auto">
        <WootButton
          v-if="selectedRowCount > 0"
          variant="clear"
          class="mr-4"
          @click="toggleSelectCancelAll"
        >
          {{ $t('BLACKLIST.CANCEL') }}
        </WootButton>
        <WootButton
          v-if="selectedRowCount > 0"
          variant="primary"
          size="small"
          class="text-center bg-blue-500 hover:bg-blue-600 text-white px-4 py-1 rounded-md mr-4"
          @click="openDeleteModal(null)"
        >
          {{ $t('BLACKLIST.BATCH_UNBLOCK') }}
        </WootButton>
        <div v-else class="w-24 h-7"></div>
      </div>
    </div>
    <Table :table="table" class="max-h-[calc(100vh-21.875rem)]" />
    <Pagination class="mt-2" :table="table" />
    <div
      v-if="isLoading"
      class="blacklist-loader flex flex-col items-center justify-center p-4"
    >
      <Spinner />
      <span class="mt-2 text-sm text-slate-600 dark:text-slate-400">{{
        $t('BLACKLIST.LOADING')
      }}</span>
    </div>
    <EmptyState
      v-else-if="!isLoading && !blacklist.length"
      :title="$t('BLACKLIST.LIST.EMPTY_RESULT.404')"
    />
    <DeleteModal
      v-if="showDeleteModal"
      v-model:show="showDeleteModal"
      :on-close="closeDeleteModal"
      :on-confirm="confirmDelete"
      :title="
        selectedBlacklistId
          ? $t('BLACKLIST.MODALS.DELETE_IP.TITLE')
          : $t('BLACKLIST.MODALS.BATCH_DELETE_IP.TITLE')
      "
      :message="
        selectedBlacklistId
          ? $t('BLACKLIST.MODALS.DELETE_IP.MESSAGE')
          : $t('BLACKLIST.MODALS.BATCH_DELETE_IP.MESSAGE')
      "
      :confirmText="$t('BLACKLIST.MODALS.DELETE.CONFIRM')"
      :rejectText="$t('BLACKLIST.MODALS.DELETE.CANCEL')"
      :has-confirm-loading="isDeleteLoading"
    />
  </div>
</template>

<style lang="scss" scoped>
.blacklist-table-container {
  overflow-x: auto; /* 启用水平滚动 */
  width: 100%;
}

// 表格样式
:deep(.woot-table) {
  border-radius: 0.375rem;
  border: 1px solid #f1f5f9;
  min-width: 800px; /* 设置最小宽度，确保在小屏幕上可以滚动 */
}

// // 表头样式
// :deep(.woot-table-header) {
//   background-color: #f8fafc;
// }
// 表格行样式
:deep(.woot-table-row) {
  transition: background-color 0.2s;

  &:hover {
    background-color: #f8fafc;
  }
}

// 按钮样式
:deep(.woot-button) {
  background-color: #3b82f6;
  color: white;
  padding: 0.25rem 1rem;
  border-radius: 0.375rem;

  &:hover {
    background-color: #2563eb;
  }
}

// 确认弹窗样式
:deep(.modal-container) {
  border-radius: 0.5rem;
  box-shadow:
    0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
}
</style>
